/**
 * Which page slugs are served from a committed local template under
 * `public/pages/<slug>/` instead of the public page API.
 *
 * The list comes from `REACT_APP_LOCAL_PAGES` — a comma-separated list of
 * slugs — so which pages are templated is a deployment setting rather than a
 * code change, and can differ per environment:
 *
 *   REACT_APP_LOCAL_PAGES=home,about-us
 *
 * Unset or empty means no page is templated and every route takes the API
 * path exactly as it did before this feature existed. That is the safe
 * default, and the default in every environment that has not opted in.
 *
 * CRA inlines REACT_APP_* variables at BUILD time, so changing this value
 * needs a dev-server restart (or a rebuild in production) — editing .env
 * while `npm start` is running has no effect until it restarts.
 *
 * Why a list at all: nginx serves this SPA with `try_files $uri $uri/
 * /index.html`, so a request for a file that does not exist returns
 * index.html with HTTP 200 rather than a 404. There is no way to detect a
 * missing template by fetching it, and a wrong guess would render the React
 * shell as page content.
 *
 * Adding a page:
 *   1. Download the template from the Page Builder HTML/CSS workspace.
 *   2. `unzip <slug>.zip -d public/pages/`
 *   3. Add the slug to REACT_APP_LOCAL_PAGES and restart/rebuild.
 *
 * Only `visibility: "public"` pages belong here — the sign-in gate for
 * private pages lives in the API response, which a static file cannot
 * reproduce. loadLocalTemplate() refuses a non-public config.json as a second
 * line of defence.
 */

import envConfig from './config/envConfig';

/**
 * Split the env value into slugs.
 *
 * Tolerant on purpose: an env file edited by hand picks up stray spaces and
 * trailing commas, and neither should turn into a slug that matches nothing
 * or, worse, an empty string that matches every route with no slug.
 */
export function parseLocalPages(value) {
  return String(value || '')
    .split(',')
    .map((slug) => slug.trim())
    .filter(Boolean);
}

export const LOCAL_PAGES = parseLocalPages(envConfig.localPages);
